import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { QrCode, Copy, Check, Clock, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface PickupTokenDisplayProps {
  orderId: string;
}

interface PickupToken {
  qr_svg: string | null;
  otp_code: string | null;
  expires_at: string;
}

const PickupTokenDisplay = ({ orderId }: PickupTokenDisplayProps) => {
  const [token, setToken] = useState<PickupToken | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    generateToken();
  }, [orderId]);

  const generateToken = async () => {
    try {
      setLoading(true);
      
      // Check order status first - only show QR if order is AVAILABLE_FOR_PICKUP or later
      const { data: orderData, error: orderError } = await supabase
        .from('orders')
        .select('status')
        .eq('id', orderId)
        .single();

      if (orderError) {
        console.error('[PickupTokenDisplay] Error fetching order:', orderError);
        throw orderError;
      }

      // Only show token if order is ready for pickup
      if (!['AVAILABLE_FOR_PICKUP', 'PICKED_UP'].includes(orderData.status)) {
        console.log('[PickupTokenDisplay] Order not ready yet, status:', orderData.status);
        setToken(null);
        setLoading(false);
        return;
      }
      
      // Fetch existing token from database
      const { data: tokenData, error: tokenError } = await supabase
        .from('pickup_tokens')
        .select('qr_svg, otp_expires_at')
        .eq('order_id', orderId)
        .single();

      if (tokenError) {
        console.error('[PickupTokenDisplay] Error fetching token:', tokenError);
        throw tokenError;
      }

      if (tokenData) {
        setToken({
          qr_svg: tokenData.qr_svg,
          otp_code: null, // OTP is hashed, cannot retrieve
          expires_at: tokenData.otp_expires_at
        });
      } else {
        throw new Error("Token not found");
      }
    } catch (error: any) {
      console.error('Error fetching token:', error);
      toast({
        title: "Erreur",
        description: error.message || "Impossible de récupérer le code de retrait",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast({
        title: "Copié !",
        description: "Le code OTP a été copié dans le presse-papiers"
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Impossible de copier le code",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </CardContent>
      </Card>
    );
  }

  if (!token) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center p-12 text-center">
          <Clock className="h-12 w-12 text-blue-500 mb-4" />
          <h3 className="font-semibold text-lg mb-2">Code de retrait en attente</h3>
          <p className="text-muted-foreground mb-4">
            Votre code QR sera disponible une fois que le vendeur aura déposé le produit au point relais.
          </p>
          <p className="text-sm text-muted-foreground">
            Vous recevrez un email dès que votre commande sera prête pour le retrait. 📧
          </p>
        </CardContent>
      </Card>
    );
  }

  const expirationDate = new Date(token.expires_at);
  const daysUntilExpiration = Math.ceil((expirationDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24));

  return (
    <div className="space-y-6">
      {/* QR Code Card */}
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="flex items-center justify-center gap-2">
            <QrCode className="h-5 w-5" />
            Code QR de retrait
          </CardTitle>
          <CardDescription>
            Présentez ce code au point relais pour récupérer votre commande
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center space-y-4">
          {token.qr_svg && (
            <div 
              dangerouslySetInnerHTML={{ __html: token.qr_svg }}
              className="w-64 h-64 flex items-center justify-center"
            />
          )}
          
          <Badge variant={daysUntilExpiration > 2 ? "default" : "destructive"}>
            <Clock className="h-3 w-3 mr-1" />
            {daysUntilExpiration > 0 
              ? `Valide encore ${daysUntilExpiration} jour${daysUntilExpiration > 1 ? 's' : ''}`
              : 'Expire bientôt'}
          </Badge>
        </CardContent>
      </Card>

      {/* OTP Code Card */}
      {token.otp_code && (
      <Card>
        <CardHeader>
          <CardTitle className="text-center">Code OTP alternatif</CardTitle>
          <CardDescription className="text-center">
            Consultez votre email pour obtenir le code à 6 chiffres
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center text-sm text-muted-foreground">
            <p>Le code OTP a été envoyé par email.</p>
            <p className="mt-2">Vous pouvez l'utiliser au point relais si vous ne pouvez pas scanner le QR code.</p>
          </div>
        </CardContent>
      </Card>
      )}
      
      {/* Instructions */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <h4 className="font-semibold text-blue-900 mb-3">Instructions de retrait :</h4>
          <ul className="space-y-2 text-sm text-blue-800">
            <li className="flex items-start">
              <span className="mr-2">1.</span>
              <span>Attendez la notification confirmant que votre commande est prête</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">2.</span>
              <span>Rendez-vous au point relais avec une pièce d'identité</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">3.</span>
              <span>Présentez le QR code ou donnez le code OTP au responsable</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">4.</span>
              <span>Récupérez votre commande après vérification</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default PickupTokenDisplay;
